import React, { useState } from 'react';
import { useLogin, useNotify, Notification } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import {
    TextField, Button, Paper, Typography, Container, Grid, InputAdornment, IconButton
} from '@material-ui/core';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { urlLogo } from '../../utils/urlBase';
import { motion } from 'framer-motion';

const useStyles = makeStyles((theme) => ({
    containerbg: {
        minHeight: '100vh',
        width: '100vw',
        background: 'linear-gradient(135deg, #41ad72 0%, #2d9cb7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
    },
    paper: {
        borderRadius: 18,
        padding: theme.spacing(4, 3),
        boxShadow: '0 4px 40px rgba(50,130,100,0.20)',
        backdropFilter: 'blur(6px)',
        background: 'rgba(255,255,255,0.95)',
        maxWidth: 420,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    logo: {
        width: 90,
        height: 'auto',
        marginBottom: theme.spacing(2),
        filter: 'drop-shadow(0 3px 18px #41ad7266)',
    },
    title: {
        fontWeight: 700,
        letterSpacing: '1px',
        marginBottom: theme.spacing(1),
        color: '#24764b',
        textShadow: '0 1px 6px #41ad7222'
    },
    subtitle: {
        color: '#626262',
        marginBottom: theme.spacing(3),
        letterSpacing: '1.5px'
    },
    form: {
        width: '100%',
        marginTop: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    inputField: {
        marginBottom: theme.spacing(2),
        '& .MuiInputBase-root': {
            background: '#f3faf6',
            borderRadius: 8,
            fontSize: '1.1rem'
        }
    },
    submit: {
        margin: theme.spacing(2, 0, 2),
        background: 'linear-gradient(90deg, #41ad72 40%, #2d9cb7 100%)',
        color: '#fff',
        fontWeight: 700,
        fontSize: '1.1rem',
        borderRadius: 8,
        letterSpacing: '1.2px',
        boxShadow: '0 2px 8px #41ad7233',
        '&:hover': {
            background: 'linear-gradient(90deg, #36955e 0%, #2a84a3 100%)',
            boxShadow: '0 4px 24px #24764b33'
        }
    }
}));

const MyLoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const login = useLogin();
    const notify = useNotify();
    const classes = useStyles();

    const handleSubmit = e => {
        e.preventDefault();
        login({ username: email, password }).catch(() =>
            notify('Email o password non valida', { type: 'error' })
        );
    };

    return (
        <div className={classes.containerbg}>
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
                <Paper elevation={0} className={classes.paper}>
                    <img src={urlLogo} alt="Logo" className={classes.logo} />
                    <Typography component="h1" variant="h5" className={classes.title}>
                        Area Amministrativa
                    </Typography>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                        Inserisci le tue credenziali
                    </Typography>
                    <form className={classes.form} onSubmit={handleSubmit} autoComplete="on">
                        <TextField
                            className={classes.inputField}
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            autoFocus
                            placeholder="Email"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlinedIcon color="primary" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            className={classes.inputField}
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            placeholder="Password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon color="primary" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            onMouseDown={e => e.preventDefault()}
                                            edge="end"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            className={classes.submit}
                        >
                            Accedi
                        </Button>
                    </form>
                </Paper>
            </motion.div>
            <Notification />
        </div>
    );
};

export default MyLoginPage;
